"use client";

import React, { useEffect, useRef } from 'react';

export interface CentreMarker {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

function loadLeaflet(): Promise<any> {
  return new Promise((resolve, reject) => {
    const w = window as any;
    if (w.L) return resolve(w.L);
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(w.L));
      existing.addEventListener('error', reject);
      if (w.L) resolve(w.L);
      return;
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(w.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function CentresMap({ centres }: { centres: CentreMarker[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !ref.current || mapRef.current || !L) return;
        const map = L.map(ref.current, { scrollWheelZoom: false });
        mapRef.current = map;

        // Fond clair et épuré (CartoDB Positron, basé OSM, sans clé) pour faire ressortir les marqueurs.
        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; OpenStreetMap &copy; CARTO',
          subdomains: 'abcd',
          maxZoom: 20,
        }).addTo(map);

        const icon = L.divIcon({
          className: '',
          html: '<div style="width:20px;height:20px;background:#DB052C;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
          popupAnchor: [0, -10],
        });

        const latlngs: [number, number][] = [];
        centres.forEach((c) => {
          const gmaps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.name + ', ' + c.address)}`;
          const popup = `<div style="font-family:Poppins,sans-serif;min-width:170px"><strong>${c.name}</strong><br><span style="color:#6B7B8D">${c.address}</span><br><a href="${gmaps}" target="_blank" rel="noopener noreferrer" style="color:#DB052C;font-weight:600">Itinéraire</a></div>`;
          L.marker([c.lat, c.lng], { icon }).addTo(map).bindPopup(popup);
          latlngs.push([c.lat, c.lng]);
        });

        if (latlngs.length > 1) {
          map.fitBounds(latlngs, { padding: [40, 40] });
        } else if (latlngs.length === 1) {
          map.setView(latlngs[0], 13);
        } else {
          map.setView([50.85, 4.42], 9);
        }
      })
      .catch(() => {
        /* CDN indisponible : la carte ne se charge pas, pas de crash */
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [centres]);

  return <div ref={ref} className="w-full h-full" style={{ minHeight: '450px' }} aria-label="Carte des centres" role="img" />;
}
